<template>
  <div class="min-h-screen">  
      <!-- Loader Tuin Component -->
      <div>
        <LoaderTuin :is-loading="loader" />
      </div>
    <div class="max-w-7xl mx-auto">
      <!-- Content - Desktop Layout (sm and up): Two columns -->
      <div v-if="propertyData && !loader" class="hidden sm:grid sm:grid-cols-2 sm:gap-6 lg:gap-8">
        
        <!-- Left Column - Property Card (fwb-card from GridPropertiesComponent) -->
        <div class="sm:col-span-1">
          <fwb-card
            v-if="propertyData"
            img-alt="Property Image"
            variant="image"
            class="w-full h-[367px] cursor-pointer"
          >
            <div class="w-full h-[184px] overflow-hidden">
              <div class="flex justify-between absolute px-4 py-3 w-auto">
                <div class="flex gap-1">
                  <div>
                    <span class="bg-custom_black2 flex text-white text-sm font-semibold rounded-md px-3 py-1 z-50">
                      {{ propertyData.type_property_name }}
                    </span>
                  </div>
                  <div>
                    <LabelTag
                      :label="propertyData.type_movement_name"
                      :type_movement_id="propertyData.type_movement_id"
                    />
                  </div>
                </div>
              </div>
              <div 
                class="w-full h-52 overflow-hidden flex rounded-t-lg"
                :class="propertyData.total_image == 0 ? 'justify-center items-center bg-[#f2f3f2]' : 'justify-end'"
              >
                <div>
                  <div
                    v-if="propertyData.total_image > 0"
                    class="w-full h-[184px] bg-cover bg-center"
                    :alt="propertyData.property_name"
                  >
                    <img
                      :src="propertyData.image"
                      :alt="propertyData.property_name"
                      class="w-full object-cover h-150 rounded-t-lg"
                    />
                  </div>

                  <div v-if="propertyData.total_image == 0">
                    <img
                      src="@/assets/imgs/default_image.svg"
                      :alt="propertyData.property_name"
                      class="w-full h-28 object-cover rounded-t-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div class="py-2 px-4 h-[181px] flex flex-col">
              <div class="flex justify-between">
                <div class="w-1/2">
                  <span :class="[
                    'text-xs font-medium rounded-md px-2.5 py-0.5',
                    propertyData.status_id === 1
                      ? 'bg-green-100 text-green-800'
                    : propertyData.status_id === 2
                      ? 'bg-gray-100 text-gray-800'
                    : 'bg-yellow-100 text-yellow-800'
                  ]">
                    {{ propertyData.status_name }}
                  </span>
                </div>
                <div class="w-1/2 text-right flex flex-col">
                  <span class="text-[8px] text-custom_gray">ID de la propiedad</span>
                  <span class="text-[10px] font-medium">{{ propertyData.property_code }}</span>
                </div>
              </div>
              <div>
                <p 
                  class="mt-2 text-base font-semibold text-black leading-5 tracking-tight property-title h-10 flex-1"
                  :title="propertyData.property_name"
                >
                  {{ propertyData.property_name }}
                </p>

                <p 
                  class="flex items-center mt-2 text-xs font-normal text-black truncate overflow-hidden whitespace-nowrap"
                  :title="propertyData.property_address"
                >
                  <img
                    src="@/assets/imgs/icons/map-pin-outline.svg"
                    alt="Ubicación"
                    class="w-4 h-4 shrink-0"
                  />
                  <span class="truncate overflow-hidden whitespace-nowrap block w-full">
                    {{ propertyData.property_address != ", , " ? propertyData.property_address : "" }}
                  </span>
                </p>
                
                <!-- Si es movimiento mixto (venta + renta) -->
                <div
                  v-if="propertyData.type_movement_id === 3"
                  class="flex flex-col gap-1 text-black text-base font-semibold mt-2"
                >
                  <div>
                    <span class="text-base font-normal">Venta </span>{{ propertyData.property_value }}
                    <span class="text-xs font-normal"> {{ propertyData.currency_code || propertyData.currency }}</span>
                  </div>
                  <div>
                    <span class="text-base font-normal">Renta </span>{{ propertyData.rent_value }}
                    <span class="text-xs font-normal"> {{ propertyData.currency_code || propertyData.currency }}</span>
                  </div>
                </div>
                <!-- Solo venta (1 o 5) -->
                <div
                  v-else-if="propertyData.type_movement_id === 1 || propertyData.type_movement_id === 5 || propertyData.type_movement_id === 2 || propertyData.type_movement_id === 4"
                  class="text-black text-base font-semibold mt-2"
                >
                  <span class="text-base font-normal">{{ propertyData.type_movement_name }} </span> {{ propertyData.property_value }}
                  <span class="text-xs font-normal">{{ propertyData.currency_code || propertyData.currency }}</span>
                </div>
              </div>
            </div>
          </fwb-card>
        </div>

        <!-- Right Column - Property Portals -->
        <div class="sm:col-span-1">
          
          <!-- Image Summary Card -->
          <div class="mb-6">
            <h3 class="text-xl font-medium text-gray-900 mb-4">¡Tu propiedad ha sido <span class="text-tuin-100">publicada!</span></h3>
            <p class="text-custom_gray mb-4 text-base font-medium">Puedes ir a visitar la propiedad que ya se publicó, o puedes ir a la sección de tus propiedades</p>
            <p class="text-gray-900 text-base font-normal mb-4">Puedes ver esta propiedad también en:</p>
            
          </div>

          <div>
            <PropertyPortalsComponent 
              :partners="partners" 
              :slug_url="slug_url" 
              :property_id="proeprtyId"
              :has_commission_sale="property.has_commission_sale"
              :has_commission_rent="property.has_commission_rent"
              :status_id="property.status_id"
              :type_movement_id="property.type_movement_id"
            />
          </div>

          <div class="mt-6 flex flex-col-1 justify-end items-end">
            <button
              class="w-full sm:w-auto bg-tuin-100 text-white py-2.5 px-5 rounded-lg hover:bg-tuin_hover text-sm font-medium transition-colors"
              @click="goToProperties()"
            >
              Ir a mis propiedades
            </button>
          </div>
        </div>
      </div>

      <!-- Content - Mobile Layout: Stacked -->
      <div v-if="propertyData && !loader" class="sm:hidden space-y-6">
        <!-- Image Summary Card -->
        <div class="bg-white mb-9">
          <h3 class="text-2xl font-medium text-gray-900 mb-4">¡Tu propiedad ha sido <span class="text-tuin-100">publicada!</span></h3>
          <p class="text-custom_gray mb-4 text-base font-medium">Puedes ir a visitar la propiedad que ya se publicó, o puedes ir a la sección de tus propiedades</p>
        </div>
        <!-- Mobile Property Card -->
        <div>
          <fwb-card
            v-if="propertyData"
            img-alt="Property Image"
            variant="image"
            class="w-full h-[367px] cursor-pointer"
          >
            <div class="w-full h-[184px] overflow-hidden">
              <div class="flex justify-between absolute px-4 py-3 w-auto">
                <div class="flex gap-1">
                  <div>
                    <span class="bg-custom_black2 flex text-white text-sm font-semibold rounded-md px-3 py-1 z-50">
                      {{ propertyData.type_property_name }}
                    </span>
                  </div>
                  <div>
                    <LabelTag
                      :label="propertyData.type_movement_name"
                      :type_movement_id="propertyData.type_movement_id"
                    />
                  </div>
                </div>
              </div>
              <div 
                class="w-full h-52 overflow-hidden flex rounded-t-lg"
                :class="propertyData.total_image == 0 ? 'justify-center items-center bg-[#f2f3f2]' : 'justify-end'"
              >
                <div>
                  <div
                    v-if="propertyData.total_image > 0"
                    class="w-full h-[184px] bg-cover bg-center"
                    :alt="propertyData.property_name"
                  >
                    <img
                      :src="propertyData.image"
                      :alt="propertyData.property_name"
                      class="w-full object-cover h-150 rounded-t-lg"
                    />
                  </div>

                  <div v-if="propertyData.total_image == 0">
                    <img
                      src="@/assets/imgs/default_image.svg"
                      :alt="propertyData.property_name"
                      class="w-full h-28 object-cover rounded-t-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div class="py-2 px-4 h-[181px] flex flex-col">
              <div class="flex justify-between">
                <div class="w-1/2">
                  <span :class="[
                    'text-xs font-medium rounded-md px-2.5 py-0.5',
                    propertyData.status_id === 1
                      ? 'bg-green-100 text-green-800'
                    : propertyData.status_id === 2
                      ? 'bg-gray-100 text-gray-800'
                    : 'bg-yellow-100 text-yellow-800'
                  ]">
                    {{ propertyData.status_name }}
                  </span>
                </div>
                <div class="w-1/2 text-right flex flex-col">
                  <span class="text-[8px] text-custom_gray">ID de la propiedad</span>
                  <span class="text-[10px] font-medium">{{ propertyData.property_code }}</span>
                </div>
              </div>
              <div>
                <p 
                  class="mt-2 text-base font-semibold text-black leading-5 tracking-tight property-title h-10 flex-1"
                  :title="propertyData.property_name"
                >
                  {{ propertyData.property_name }}
                </p>

                <p 
                  class="flex items-center mt-2 text-xs font-normal text-black truncate overflow-hidden whitespace-nowrap"
                  :title="propertyData.property_address"
                >
                  <img
                    src="@/assets/imgs/icons/map-pin-outline.svg"
                    alt="Ubicación"
                    class="w-4 h-4 shrink-0"
                  />
                  <span class="truncate overflow-hidden whitespace-nowrap block w-full">
                    {{ propertyData.property_address != ", , " ? propertyData.property_address : "" }}
                  </span>
                </p>
                
                <!-- Si es movimiento mixto (venta + renta) -->
                <div
                  v-if="propertyData.type_movement_id === 3"
                  class="flex flex-col gap-1 text-black text-base font-semibold mt-2"
                >
                  <div>
                    <span class="text-base font-normal">Venta </span>{{ propertyData.property_value }}
                    <span class="text-xs font-normal"> {{ propertyData.currency_code || propertyData.currency }}</span>
                  </div>
                  <div>
                    <span class="text-base font-normal">Renta </span>{{ propertyData.rent_value }}
                    <span class="text-xs font-normal"> {{ propertyData.currency_code || propertyData.currency }}</span>
                  </div>
                </div>
                <!-- Solo venta (1 o 5) -->
                <div
                  v-else-if="propertyData.type_movement_id === 1 || propertyData.type_movement_id === 5 || propertyData.type_movement_id === 2 || propertyData.type_movement_id === 4"
                  class="text-black text-base font-semibold mt-2"
                >
                  <span class="text-base font-normal">{{ propertyData.type_movement_name }} </span> {{ propertyData.property_value }}
                  <span class="text-xs font-normal">{{ propertyData.currency_code || propertyData.currency }}</span>
                </div>
              </div>
            </div>
          </fwb-card>
        </div>
        <!-- Image Summary Card -->
        <div class="bg-white mb-6">
          <p class="text-gray-900 text-base font-normal mb-4">Puedes ver esta propiedad también en:</p>
        </div>
        <!-- Mobile Property Portals -->
        <div class="bg-white">
          <PropertyPortalsComponent 
            :partners="partners" 
            :slug_url="slug_url" 
            :property_id="proeprtyId"
            :has_commission_sale="property.has_commission_sale"
            :has_commission_rent="property.has_commission_rent"
            :status_id="property.status_id"
            :type_movement_id="property.type_movement_id"
          />
        </div>
        <div class="mt-6 flex flex-col-1 justify-end items-end">
          <button
            class="w-full sm:w-auto bg-tuin-100 text-white py-2.5 px-5 rounded-lg hover:bg-tuin_hover text-sm font-medium transition-colors"
            @click="goToProperties()"
          >
            Ir a mis propiedades
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { FwbCard} from 'flowbite-vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import axios from 'axios'
import PropertyPortalsComponent from './PropertyPortalsComponent.vue'
import LabelTag from '@/commond/LabelTag.vue'
import LoaderTuin from '@/commond/LoaderTuin.vue'

// Composables
const route = useRoute()
const store = useStore()
const session = computed(() => store.getters.getUserSession)

// Props
const props = defineProps({
  propertyId: {
    type: [Number, String],
    required: false
  }
})

// Reactive data
const propertyData = ref(null)
const loading = ref(true)
const error = ref(null)

// Additional reactive data that the API expects
const property = ref(null)
const slug_url = ref('')
const selectedStatus = ref(1)
const loader = ref(true)
const pictures = ref([])
const description = ref('')
const characters_additional = ref('')
const partners = ref([])


// Get property ID from route params or props
const id = props.propertyId || route.params.property_id || route.params.id


// API call to get property data
async function getProperty(){
  try {
    loading.value = true
    let options = {
      headers: {
        Authorization: `Bearer ${session.value.token_api}`,
      },
    };

    let link = `${process.env.VUE_APP_API_BASE_URL}/broker/detail/property/${session.value.id}/${id}`;

    const response = await axios.get(link, options);

    console.log("✅ Respuesta completa de la API:", response.data);
    
    if (response.status == 200) {
      property.value = response.data.info;
      slug_url.value = response.data.resp.general.slug_url;
      
      // Establecer el estado seleccionado basado en el status_id de la propiedad
      selectedStatus.value = property.value.status_id || 1;
      
      loader.value = false;
      loading.value = false;

      pictures.value = response.data.resp.images;

      description.value = property.value.property_description;
      characters_additional.value = property.value.characteristics;
      partners.value = response.data.resp.partners;

      // Transform API data to propertyData for the template
      const resp = response.data.resp || {}
      const general = resp.general || {}

      // Helper function to format price
      const formatPrice = (price) => {
        if (!price) return "$0"
        const numPrice = typeof price === 'string' ? parseFloat(price.replace(/[^0-9.-]+/g, "")) : price
        return numPrice ? `$${numPrice.toLocaleString('en-US')}` : "$0"
      }

      // Transform API data to match component structure
      propertyData.value = {
        property_id: property.value.id || property.value.property_id,
        property_name: property.value.property_name || general.property_name || "Sin título",
        property_code: property.value.property_code || general.property_code || "N/A",
        type_property_name: property.value.type_property?.name || property.value.property_type || general.type_property_name || "N/A",
        type_movement_name: property.value.type_operation?.name || property.value.property_movement || general.type_movement_name || "N/A",
        type_movement_id: property.value.type_movement_id || general.type_movement_id || 1,
        status_id: property.value.status_id,
        status_name: property.value.status_name || "Activa",
        property_value: formatPrice(property.value.property_value || general.property_value),
        currency_code: property.value.currency || general.currency || "MXN",
        currency: property.value.currency || general.currency || "MXN",
        rent_value: formatPrice(property.value.rent_value || general.rent_value),
        constructed_metres: property.value.constructed_metres || general.constructed_metres || 0,
        bedrooms: property.value.bedrooms || general.bedrooms || 0,
        bathrooms: property.value.bathrooms || general.bathrooms || 0,
        property_address: property.value.address || property.value.property_address || general.property_address || "Dirección no disponible",
        image: pictures.value[0]?.src || null,
        total_image: resp.images?.length || 0,
        total_leads: property.value.total_leads || 0,
        total_views: property.value.total_views || 0,
        total_click_call: property.value.total_click_call || 0,
        total_click_wp: property.value.total_click_wp || 0,
        is_associated: property.value.is_associated || false,
        agent_name: property.value.agent_name || ""
      }
      
      console.log("Datos mapeados:", propertyData.value)
      error.value = null
    }
  } catch (err) {
    console.error("Error fetching data:", err);
    error.value = "Error al cargar los datos de la propiedad"
    loading.value = false
  }
}

// Navigation functions
function goToProperties() {
  window.location.href = '/properties'
}

// Load property data on component mount
onMounted(() => {
  if (id) {
    getProperty()
  } else {
    error.value = "ID de propiedad no encontrado"
    loading.value = false
  }
})
</script>

<style scoped>
/* Custom styles if needed */
.property-title {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
